import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence, useReducedMotion } from "framer-motion"
import { Phone, Mail, MapPin, Check, ArrowRight } from "lucide-react"
import LogoWatermark from "./LogoWatermark"

const EASE = [0.22, 1, 0.36, 1]
const GOLD = "#C9A24D"

const INITIAL = {
  fullName: "",
  need: "",
  state: "",
  city: "",
  phone: "",
  email: "",
  vehicleDetails: "",
  vin: "",
  message: "",
}

const FIELDS = [
  { id: "fullName", label: "Full Name", type: "text" },
  {
    id: "need",
    label: "Enquiry",
    type: "select",
    options: [
      ["ecu-remapping", "ECU Remapping"],
      ["scanning-diagnostics", "Scanning & Diagnostics"],
      ["mechanical", "Mechanical"],
      ["paint-shop", "Paint Shop"],
      ["performance", "Performance"],
      ["restorations", "Restorations"],
    ],
  },
  { id: "state", label: "State", type: "text" },
  { id: "city", label: "City", type: "text" },
  { id: "phone", label: "Phone Number", type: "tel" },
  { id: "email", label: "Email", type: "email" },
  { id: "vehicleDetails", label: "Vehicle Details", type: "text" },
  { id: "vin", label: "VIN", type: "text" },
  { id: "message", label: "Message", type: "textarea", span: true },
]

const contacts = [
  { icon: Phone, label: "Pune", value: "+91 9112195999", href: "tel:+919112195999" },
  { icon: Phone, label: "Bengaluru", value: "+91 99866 24530", href: "tel:+919986624530" },
  { icon: Mail, label: "Email", value: "info@ajpgroup.com", href: "mailto:info@ajpgroup.com" },
  { icon: MapPin, label: "Workshops", value: "Pune & Bengaluru, India" },
]

/* words slide up from behind a mask */
function MaskedLine({ text, delay = 0, className = "" }) {
  return (
    <span className={`block ${className}`} aria-label={text}>
      {text.split(" ").map((w, i) => (
        <span key={i} className="inline-block overflow-hidden pr-[0.25em] align-bottom" aria-hidden>
          <motion.span
            className="inline-block"
            initial={{ y: "115%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: delay + i * 0.08 }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  )
}

/* floating-label field */
function Field({ f, value, error, onChange, index }) {
  const base =
    "peer w-full rounded-lg border bg-white/[0.04] px-3 pb-2 pt-6 text-sm text-white placeholder-transparent outline-none transition-colors duration-300 focus:bg-white/[0.07] " +
    (error ? "border-red-500/70" : "border-white/15 focus:border-[#C9A24D]")

  const floated = "top-2 text-[10px] uppercase tracking-[0.18em]"
  const label = `pointer-events-none absolute left-3 text-gray-400 transition-all duration-300 ${
    f.type === "select"
      ? floated
      : "top-2 text-[10px] uppercase tracking-[0.18em] peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-focus:top-2 peer-focus:text-[10px] peer-focus:uppercase peer-focus:tracking-[0.18em] peer-focus:text-[#C9A24D]"
  }`

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.6, ease: EASE, delay: 0.1 + index * 0.05 }}
      className={f.span ? "md:col-span-2" : ""}
    >
      <div className="relative overflow-hidden rounded-lg">
        {f.type === "textarea" ? (
          <textarea
            id={f.id}
            name={f.id}
            rows={4}
            placeholder=" "
            value={value}
            onChange={onChange}
            className={`${base} resize-none`}
          />
        ) : f.type === "select" ? (
          <select id={f.id} name={f.id} value={value} onChange={onChange} className={`${base} appearance-none`}>
            <option value="" className="bg-[#1A1A1A]">
              Select an option
            </option>
            {f.options.map(([v, l]) => (
              <option key={v} value={v} className="bg-[#1A1A1A]">
                {l}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={f.id}
            name={f.id}
            type={f.type}
            placeholder=" "
            value={value}
            onChange={onChange}
            className={base}
          />
        )}
        <label htmlFor={f.id} className={label}>
          {f.label} *
        </label>
        {/* animated focus underline */}
        <span className="pointer-events-none absolute bottom-0 left-0 h-[2px] w-full origin-left scale-x-0 bg-gradient-to-r from-[#C9A24D] to-red-500 transition-transform duration-500 peer-focus:scale-x-100" />
      </div>
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -4, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-1 text-xs text-red-400"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

const ContactSection = () => {
  const reduce = useReducedMotion()
  const [form, setForm] = useState(INITIAL)
  const [errors, setErrors] = useState({})
  const [sent, setSent] = useState(false)
  const [shake, setShake] = useState(0)
  const timer = useRef(null)
  const card = useRef(null)

  useEffect(() => () => clearTimeout(timer.current), [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((p) => ({ ...p, [name]: value }))
    if (errors[name]) setErrors((p) => ({ ...p, [name]: undefined }))
  }

  const validate = () => {
    const err = {}
    FIELDS.forEach((f) => {
      if (!form[f.id].trim()) err[f.id] = "Required"
    })
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) err.email = "Enter a valid email"
    return err
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const err = validate()
    setErrors(err)
    if (Object.keys(err).length) {
      setShake((n) => n + 1)
      return
    }
    setSent(true)
    timer.current = setTimeout(() => {
      setForm(INITIAL)
      setErrors({})
      setSent(false)
    }, 3000)
  }

  // cursor spotlight on the form card
  const handleMove = (e) => {
    const el = card.current
    if (!el) return
    const r = el.getBoundingClientRect()
    el.style.setProperty("--mx", `${e.clientX - r.left}px`)
    el.style.setProperty("--my", `${e.clientY - r.top}px`)
  }

  return (
    <section className="relative isolate overflow-hidden bg-[#0b0b0d] px-6 py-24 text-white md:py-32">
      <style>{`
        @keyframes ajpShake {
          0%,100% { transform: translateX(0); }
          20% { transform: translateX(-8px); } 40% { transform: translateX(7px); }
          60% { transform: translateX(-5px); } 80% { transform: translateX(3px); }
        }
        @keyframes ajpShine { 0% { transform: translateX(-120%) skewX(-20deg); } 45%, 100% { transform: translateX(380%) skewX(-20deg); } }
      `}</style>

      {/* ---------- BACKGROUND ---------- */}
      <div
        className="absolute inset-0 -z-20 bg-cover bg-center opacity-25"
        style={{ backgroundImage: "url('/assets/contact.jpg')" }}
      />
      <div className="absolute inset-0 -z-20 bg-gradient-to-b from-[#0b0b0d] via-[#0b0b0d]/70 to-[#0b0b0d]" />
      {/* faint grid */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />
      {/* drifting glows */}
      <motion.div
        aria-hidden
        className="absolute -left-32 top-10 -z-10 h-[420px] w-[420px] rounded-full bg-[#C9A24D]/20 blur-[120px]"
        animate={reduce ? undefined : { x: [0, 80, 0], y: [0, 40, 0] }}
        transition={{ duration: 16, ease: "easeInOut", repeat: Infinity }}
      />
      <motion.div
        aria-hidden
        className="absolute -right-24 bottom-0 -z-10 h-[460px] w-[460px] rounded-full bg-red-600/20 blur-[130px]"
        animate={reduce ? undefined : { x: [0, -70, 0], y: [0, -50, 0] }}
        transition={{ duration: 18, ease: "easeInOut", repeat: Infinity }}
      />
      {/* the AJP logo, big, in the background */}
      <LogoWatermark opacity={0.28} />

      {/* ---------- CONTENT ---------- */}
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
        {/* Left */}
        <div>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.4em] text-[#C9A24D]"
          >
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
              className="h-px w-10 origin-left bg-[#C9A24D]"
            />
            Contact
          </motion.p>

          <h2 className="mb-6 text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
            <MaskedLine text="Let's Connect" delay={0.1} />
            <MaskedLine
              text="With AJP Group"
              delay={0.3}
              className="bg-gradient-to-r from-white via-[#e8c97a] to-[#C9A24D] bg-clip-text text-transparent"
            />
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
            className="max-w-xl text-lg leading-relaxed text-gray-300"
          >
            Whether you're looking for partnership, store information, or business inquiries — our team
            is ready to assist you.
          </motion.p>

          <div className="mt-10 grid gap-3 sm:grid-cols-2">
            {contacts.map(({ icon: Icon, label, value, href }, i) => {
              const Tag = href ? motion.a : motion.div
              return (
                <Tag
                  key={label}
                  href={href}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, ease: EASE, delay: 0.6 + i * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-md transition-colors duration-300 hover:border-[#C9A24D]/60"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#C9A24D]/15 text-[#C9A24D] transition-colors duration-300 group-hover:bg-[#C9A24D] group-hover:text-black">
                    <Icon size={20} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-gray-500">{label}</span>
                    <span className="block truncate text-sm font-medium text-white">{value}</span>
                  </span>
                </Tag>
              )
            })}
          </div>
        </div>

        {/* Right – glass form card with a rotating gold border */}
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.96 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: EASE, delay: 0.15 }}
        >
          <div
            key={shake}
            style={shake ? { animation: "ajpShake 0.5s" } : undefined}
            className="relative overflow-hidden rounded-2xl border border-white/15"
          >
<div
              ref={card}
              onMouseMove={handleMove}
              className="group relative rounded-2xl bg-[#101012]/55 p-7 backdrop-blur-[6px] sm:p-10"
            >
              {/* cursor spotlight */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(420px circle at var(--mx,50%) var(--my,50%), rgba(201,162,77,0.13), transparent 45%)",
                }}
              />

              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  >
                    <motion.span
                      initial={{ scale: 0, rotate: -90 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.1 }}
                      className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#C9A24D] text-black shadow-[0_0_50px_rgba(201,162,77,0.5)]"
                    >
                      <Check size={40} strokeWidth={3} />
                    </motion.span>
                    <h3 className="text-2xl font-semibold">Thank you!</h3>
                    <p className="mt-2 max-w-xs text-sm text-gray-400">
                      Your message has been received. Our team will get back to you shortly.
                    </p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    noValidate
                    className="relative"
                  >
                    <h3 className="mb-1 text-2xl font-semibold">Let's Get Started</h3>
                    <p className="mb-8 text-sm text-gray-400">Tell us about your car and what you need.</p>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {FIELDS.map((f, i) => (
                        <Field
                          key={f.id}
                          f={f}
                          index={i}
                          value={form[f.id]}
                          error={errors[f.id]}
                          onChange={handleChange}
                        />
                      ))}
                    </div>

                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                      className="group/btn relative mt-6 flex w-full items-center justify-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-red-600 to-red-700 py-3.5 text-base font-semibold shadow-[0_10px_40px_-10px_rgba(220,38,38,0.7)]"
                    >
                      <span
                        aria-hidden
                        className="absolute inset-y-0 left-0 w-1/3 bg-white/25"
                        style={{ animation: reduce ? "none" : "ajpShine 2s ease-in-out infinite" }}
                      />
                      <span className="relative">Submit Now</span>
                      <ArrowRight size={18} className="relative transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default ContactSection
